// QR menü tema şablonları — tek doğruluk kaynağı.
// Varsayılan renkler/tipografi her zaman buradaki şablondan gelir;
// kullanıcı panelden isteğe bağlı olarak üzerine yazabilir.
export interface QrThemePreset {
  id: string;
  name: string;
  // --- Renk & tipografi ---
  primary: string;
  secondary: string;
  background: string;
  font: string;
  radius: number;
  buttonStyle: "sharp" | "rounded" | "pill";
  cardStyle: "flat" | "elevated" | "glass";
  textColor: string;
  // --- Görünüm & davranış (Görsel Arayüz sekmesi) ---
  showSearchBar: boolean;
  showCategoryIcons: boolean;
  showFeaturedProducts: boolean;
  stickyCategories: boolean;
  enableAnimations: boolean;
  // --- Menü detay filtreleri (Gelişmiş sekmesi) ---
  hidePrices: boolean;
  hideUnavailable: boolean;
  showAllergens: boolean;
  showCalories: boolean;
}

export const QR_THEME_PRESETS: QrThemePreset[] = [
  {
    id: "signature",
    name: "Vidyon Signature",
    primary: "#D97706", // Amber 600 vurgu
    secondary: "#18181B", // Zinc 900 başlık
    background: "#FFFFFF", // Beyaz zemin
    font: "sans-serif",
    radius: 28,
    buttonStyle: "pill",
    cardStyle: "elevated",
    textColor: "#18181B",
    showSearchBar: true,
    showCategoryIcons: true,
    showFeaturedProducts: true,
    stickyCategories: true,
    enableAnimations: true,
    hidePrices: false,
    hideUnavailable: true,
    showAllergens: true,
    showCalories: true,
  },
];

// Aktif varsayılan tema (şimdilik tek tema mevcut)
export const DEFAULT_QR_THEME = QR_THEME_PRESETS[0];

// --- Yazı tipi seçenekleri ---
// id'ler kayıtlı ayarlarla geriye uyumlu ("sans-serif" | "serif" | "monospace" eski kayıtlar).
// googleFamily verilen fontlar ilk kullanımda Google Fonts'tan dinamik yüklenir;
// stack her zaman sistem fallback'leri içerir.
export interface QrFontOption {
  id: string;
  name: string;
  stack: string;
  googleFamily?: string;
}

export const QR_FONT_OPTIONS: QrFontOption[] = [
  {
    id: "sans-serif",
    name: "Sistem Sans (Varsayılan)",
    stack: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },
  {
    id: "inter",
    name: "Inter — Modern & Net",
    stack: "'Inter', ui-sans-serif, system-ui, sans-serif",
    googleFamily: "Inter:wght@400;600;700;800;900",
  },
  {
    id: "poppins",
    name: "Poppins — Yumuşak & Sıcak",
    stack: "'Poppins', ui-sans-serif, system-ui, sans-serif",
    googleFamily: "Poppins:wght@400;600;700;800;900",
  },
  {
    id: "montserrat",
    name: "Montserrat — Şık & Geometrik",
    stack: "'Montserrat', ui-sans-serif, system-ui, sans-serif",
    googleFamily: "Montserrat:wght@400;600;700;800;900",
  },
  {
    id: "serif",
    name: "Playfair Display — Klasik Serif",
    stack: "'Playfair Display', Georgia, 'Times New Roman', serif",
    googleFamily: "Playfair+Display:wght@400;600;700;800;900",
  },
  {
    id: "lora",
    name: "Lora — Zarif Serif",
    stack: "'Lora', Georgia, serif",
    googleFamily: "Lora:wght@400;600;700",
  },
  {
    id: "monospace",
    name: "JetBrains Mono — Minimal Mono",
    stack: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
    googleFamily: "JetBrains+Mono:wght@400;600;700;800",
  },
];

export function getQrFontStack(fontId: string): string {
  return QR_FONT_OPTIONS.find((f) => f.id === fontId)?.stack ?? QR_FONT_OPTIONS[0].stack;
}

// Seçili fontun Google Fonts stylesheet'ini (bir kez) sayfaya ekler
export function ensureQrFontLoaded(fontId: string): void {
  if (typeof document === "undefined") return;
  const opt = QR_FONT_OPTIONS.find((f) => f.id === fontId);
  if (!opt?.googleFamily) return;
  const linkId = `qr-font-${opt.id}`;
  if (document.getElementById(linkId)) return;
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${opt.googleFamily.replace(/ /g, "+")}&display=swap`;
  document.head.appendChild(link);
}
